import { useEffect, useRef, useState } from 'react';

/**
 * Checkbox animata con feedback visivo morbido:
 *  - cerchio grigio quando aperto, riempimento verde quando fatto
 *  - segno di spunta "disegnato" in ~200ms via stroke-dashoffset
 *  - rispetta prefers-reduced-motion (transizioni quasi istantanee)
 *
 * Props:
 *  - checked: boolean
 *  - onToggle: (nextValue: boolean) => void  ← passa next al caller
 *  - size: 16 | 18 | 20 (default 18)
 *  - disabled: nessun toggle ma mostra stato (es. KR numerici)
 *  - label: testo del task per screen reader (aria-label) — senza, uno
 *    screen reader annuncia solo "casella selezionata"
 *  - title, testid
 *
 * NOTE: il caller fa l'optimistic update + persiste in background.
 */
const DURATION_MS = 200;

function isReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function Checkbox({ checked, onToggle, size = 18, disabled = false, title, label, testid }) {
  const [animating, setAnimating] = useState(false);
  const wasChecked = useRef(checked);
  const reduce = isReducedMotion();

  useEffect(() => {
    if (wasChecked.current === false && checked === true) {
      // appena diventato true → trigger animazione
      setAnimating(true);
      const t = setTimeout(() => setAnimating(false), reduce ? 20 : DURATION_MS + 60);
      wasChecked.current = checked;
      return () => clearTimeout(t);
    }
    wasChecked.current = checked;
  }, [checked, reduce]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onToggle && onToggle(!checked);
  };

  // Path “check”: M5 9 L8 12 L13 6 — disegna un segno di spunta
  const checkPathLen = 14; // lunghezza approssimativa
  const dashOffset = checked ? 0 : checkPathLen;
  const transition = reduce
    ? 'none'
    : `stroke-dashoffset ${DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), fill 180ms ease-out, stroke 180ms ease-out`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={
        'shrink-0 inline-flex items-center justify-center select-none ' +
        (disabled ? 'cursor-default opacity-70' : 'cursor-pointer hover:scale-110 active:scale-95')
      }
      style={{ width: size, height: size, transition: reduce ? 'none' : 'transform 120ms ease-out' }}
      title={title}
      data-testid={testid}
      aria-checked={checked}
      aria-label={label || title || undefined}
      role="checkbox"
      data-animating={animating ? '1' : '0'}
    >
      <svg viewBox="0 0 18 18" width={size} height={size} aria-hidden>
        <circle
          cx="9"
          cy="9"
          r="8"
          fill={checked ? 'var(--qp-success)' : 'none'}
          stroke={checked ? 'var(--qp-success)' : 'var(--qp-text-mute)'}
          strokeWidth="1.4"
          style={{ transition }}
        />
        <path
          d="M5 9 L8 12 L13.5 6"
          fill="none"
          stroke="var(--qp-canvas)"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={checkPathLen}
          strokeDashoffset={dashOffset}
          style={{ transition }}
        />
      </svg>
    </button>
  );
}
